import React from 'react';
import { Chip, createFilterOptions } from '@mui/material';
import { AttachFile, ThumbUp, Done } from '@mui/icons-material';
import { SubscriptionContact } from 'store/api/models';
import { renderContactGroups } from './useProjectForm';
import { FormikHelpers, getIn } from 'formik';
import { ContactTooltip } from 'components/ContactTooltip';
import { NoMaxWidthTooltip } from '@centerpiece/components';
import { collapseTextChangeRangesAcrossMultipleVersions } from 'typescript';
import { useContacts } from './jfield/useContacts';

export const useEmailForm = ({
  contacts = [],
  onDownload,
  setOpenContact,
  preview = false,
  reply = false,
  replied = false,
  forward = false,
  ccbcc: showCCBCC = true,
  noResponseDue = false,
  disabled,
  additionalHeaderFields = [],
  messageBodyForm = [],
  editing,
}: {
  contacts?: any[];
  onDownload: Function;
  setOpenContact?: Function;
  preview?: boolean;
  reply?: boolean;
  replied?: boolean;
  forward?: boolean;
  disabled?: boolean;
  ccbcc?: boolean;
  noResponseDue?: boolean;
  additionalHeaderFields?: any[];
  messageBodyForm?: any[];
  editing?: boolean;
}) => {
  const orgpreview = preview;
  preview = preview || reply;
  const recipientForm = useContacts({
    name: 'recipients',
    label: 'To',
    setOpenContact,
  });
  const ccForm = useContacts({ name: 'cc', label: 'Cc', setOpenContact });
  const bccForm = useContacts({ name: 'bcc', label: 'Bcc', setOpenContact });
  if (preview || replied) {
    return [
      {
        name: 'from',
        label: 'From',
        itemType: 'autocomplete',
        multiple: true,
        disabled: true,
        jItemProps: {
          variant: 'standard',
          renderTags: (value: string[] | undefined, getTagProps: any) => {
            return value?.map((option: any, index: number) => (
              <NoMaxWidthTooltip title={<ContactTooltip {...option} />}>
                <Chip
                  variant="outlined"
                  color="secondary"
                  label={typeof option === 'string' ? option : option.name}
                  size="small"
                  {...getTagProps({ index })}
                  {...(preview || replied
                    ? { onDelete: undefined, disabled: false }
                    : {})}
                />
              </NoMaxWidthTooltip>
            ));
          },
        },
      },
      { ...recipientForm, disabled: true },
      ...(showCCBCC
        ? [
            {
              precondition: (values: any) => {
                const ccbcc = getIn(values, 'form.CCBCC');
                const cc = getIn(values, 'cc');
                return (
                  Array.isArray(ccbcc) &&
                  ccbcc.find(c => c === 'CC') &&
                  !!cc &&
                  Array.isArray(cc) &&
                  cc.length > 0
                );
              },
              ...ccForm,
              gridProps: (values: any) => {
                const ccbcc = getIn(values, 'form.CCBCC');
                return Array.isArray(ccbcc) && !ccbcc.find(c => c === 'BCC')
                  ? { xs: 11 }
                  : { xs: 12 };
              },
              disabled: true,
            },
            {
              precondition: (values: any) => {
                const ccbcc = getIn(values, 'form.CCBCC');
                const bcc = getIn(values, 'bcc');
                return (
                  !replied &&
                  !orgpreview &&
                  Array.isArray(ccbcc) &&
                  ccbcc.find(c => c === 'BCC') &&
                  !!bcc &&
                  Array.isArray(bcc) &&
                  bcc.length > 0
                );
              },
              ...bccForm,
              gridProps: (values: any) => {
                const ccbcc = getIn(values, 'form.CCBCC');
                return Array.isArray(ccbcc) && !ccbcc.find(c => c === 'CC')
                  ? { xs: 11 }
                  : { xs: 12 };
              },
              disabled: true,
            },
            {
              precondition: (values: any) => {
                const ccbcc = getIn(values, 'form.CCBCC');
                const bcc = getIn(values, 'bcc');
                return (
                  !replied &&
                  orgpreview &&
                  Array.isArray(ccbcc) &&
                  ccbcc.find(c => c === 'BCC') &&
                  !!bcc &&
                  Array.isArray(bcc) &&
                  bcc.length > 0
                );
              },
              name: 'bccblank',
              itemType: 'textfield',
              label: 'Bcc',
              defaultValue: '(BCC recipients will only see themselves)',
              gridProps: (values: any) => {
                const ccbcc = getIn(values, 'form.CCBCC');
                return Array.isArray(ccbcc) && !ccbcc.find(c => c === 'CC')
                  ? { xs: 11 }
                  : { xs: 12 };
              },
              disabled: true,
            },
          ]
        : []),
      // {
      //   name: 'recipients',
      //   label: 'To',
      //   itemType: 'autocomplete',
      //   multiple: true,
      //   disabled: true,
      //   jItemProps: {
      //     variant: 'standard',
      //     renderTags: (value: string[] | undefined, getTagProps: any) => {
      //       return value?.map((option: any, index: number) => (
      //         <NoMaxWidthTooltip title={<ContactTooltip {...option} />}>
      //           <Chip
      //             variant="outlined"
      //             color="secondary"
      //             label={typeof option === 'string' ? option : option.name}
      //             size="small"
      //             {...getTagProps({ index })}
      //             {...(preview || replied
      //               ? { onDelete: undefined, disabled: false }
      //               : {})}
      //           />
      //         </NoMaxWidthTooltip>
      //       ));
      //     },
      //   },
      // },
      ...(forward
        ? [
            {
              name: 'fwd',
              label: 'Forward',
              itemType: 'emailfwd',
              multiple: true,
              disabled: false,
              freeSolo: true,
              freeSoloFormat: (value: any) => ({
                id: 0,
                email: value,
                label: value,
              }),
              jItemProps: { disableCC: true },
            },
          ]
        : []),
      {
        name: 'subject',
        itemType: 'textfield',
        jItemProps: {
          variant: 'standard',
        },
        label: 'Subject',
        gridProps: (values: any) =>
          values?.due_date ? { xs: 12, sm: 8 } : { xs: 12 },
        disabled: true,
      },
      {
        precondition: (values: any) => values?.due_date,
        name: 'due_date',
        itemType: 'datetimepicker',
        label: 'Due by',
        gridProps: { xs: 12, sm: 4 },
        jItemProps: {
          variant: 'standard',
        },
        disabled: true,
      },
      ...additionalHeaderFields,
      {
        name: 'body-title',
        label: 'Email Message ',
        itemType: 'typography',
        disabled: true,
        jItemProps: {
          component: 'div',
          variant: 'caption',
          sx: { paddingTop: 1 },
        },
        gridProps: { xs: 9 },
      },
      ...messageBodyForm,
      {
        precondition: (values: any) =>
          (!preview || reply) && !disabled && !replied,
        name: '',
        itemType: 'button',
        jItemProps: { type: 'submit' },
        label: 'SEND',
        gridProps: { xs: 6, mx: 'auto' },
      },
    ];
  } else {
    return [
      {
        name: 'title',
        label: 'Email Details',
        itemType: 'typography',
        jItemProps: { component: 'div', variant: 'caption' },
      },
      {
        name: 'title-bottom',
        label: '',
        itemType: 'divider',
        gridProps: {
          sx: { paddingTop: '0px !important', paddingBottom: 1 },
        },
      },
      {
        ...recipientForm,
        label: 'To',
        gridProps: (values: any) => {
          const ccbcc = getIn(values, 'form.CCBCC');
          return showCCBCC &&
            (!ccbcc || (Array.isArray(ccbcc) && ccbcc.length === 0))
            ? { xs: 10 }
            : { xs: 12 };
        },
      },
      ...(showCCBCC
        ? [
            {
              precondition: (values: any) => {
                const ccbcc = getIn(values, 'form.CCBCC');
                return !ccbcc || (Array.isArray(ccbcc) && ccbcc.length === 0);
              },
              name: 'form.CCBCC',
              itemType: 'toggle_group',
              label: '',
              options: [
                { label: 'CC', value: 'CC' },
                { label: 'BCC', value: 'BCC' },
              ],
              gridProps: { xs: 1 },
            },
            {
              precondition: (values: any) => {
                const ccbcc = getIn(values, 'form.CCBCC');
                return Array.isArray(ccbcc) && ccbcc.find(c => c === 'CC');
              },
              ...ccForm,
              gridProps: (values: any) => {
                const ccbcc = getIn(values, 'form.CCBCC');
                return Array.isArray(ccbcc) && !ccbcc.find(c => c === 'BCC')
                  ? { xs: 11 }
                  : { xs: 12 };
              },
            },
            {
              precondition: (values: any) => {
                const ccbcc = getIn(values, 'form.CCBCC');
                return Array.isArray(ccbcc) && !ccbcc.find(c => c === 'BCC');
              },
              name: 'form.CCBCC',
              itemType: 'toggle_group',
              label: '',
              options: [
                // { label: 'CC', value: 'CC' },
                { label: 'BCC', value: 'BCC' },
              ],
              gridProps: { xs: 1 },
            },
            {
              precondition: (values: any) => {
                const ccbcc = getIn(values, 'form.CCBCC');
                return (
                  !replied &&
                  Array.isArray(ccbcc) &&
                  ccbcc.find(c => c === 'BCC')
                );
              },
              ...bccForm,
              gridProps: (values: any) => {
                const ccbcc = getIn(values, 'form.CCBCC');
                return Array.isArray(ccbcc) && !ccbcc.find(c => c === 'CC')
                  ? { xs: 11 }
                  : { xs: 12 };
              },
            },
            {
              precondition: (values: any) => {
                const ccbcc = getIn(values, 'form.CCBCC');
                return Array.isArray(ccbcc) && !ccbcc.find(c => c === 'CC');
              },
              name: 'form.CCBCC',
              itemType: 'toggle_group',
              label: '',
              options: [
                { label: 'CC', value: 'CC' },
                // { label: 'BCC', value: 'BCC' },
              ],
              gridProps: { xs: 1 },
            },
          ]
        : []),
      {
        name: 'subject',
        itemType: 'textfield',
        label: 'Add email subject line',
        gridProps: noResponseDue ? { xs: 12 } : { xs: 12, sm: 8 },
        disabled: disabled ? disabled : preview,
      },
      {
        precondition: () => !noResponseDue,
        name: 'due_date',
        itemType: 'datepicker',
        label: 'Add response due date',
        gridProps: { xs: 12, sm: 4 },
        disabled: disabled ? disabled : preview,
        jItemProps: {
          datetimeformat: true,
        },
      },
      ...additionalHeaderFields,
      {
        name: 'body-title',
        label: 'Email Message ',
        itemType: 'typography',
        disabled: disabled,
        jItemProps: {
          component: 'div',
          variant: 'caption',
          sx: { paddingTop: 1 },
        },
        gridProps: { xs: 8 },
      },
      {
        // precondition: () => !noResponseDue,
        name: 'form.auto_salutation',
        itemType: 'checkbox',
        label: 'Auto greeting',
        jItemProps: {
          sx: { fontSize: '14px !important', overflowX: 'hidden' },
          onChange: ({
            values,
            name,
            setFieldValue,
            checked,
          }: {
            values: any;
            name: string;
            setFieldValue: Function;
            checked: boolean;
          }) => {
            setFieldValue('form.auto_salutation', checked ? true : undefined);
            if (checked) {
              let body = values?.form?.body || [{ text: '' }];
              body.unshift({
                type: 'auto_salutation',
                text: 'Hey {{first_name}},\n',
                disabled: true,
              });
              setFieldValue('form.body', body);
            } else {
              let body = values?.form?.body || [];
              if (body?.[0]?.type === 'auto_salutation') {
                body.shift();
                setFieldValue('form.body', body);
              }
            }
          },
        },
        gridProps: {
          xs: 4,
          sx: { fontSize: '14px !important', overflowX: 'hidden' },
        },
      },
      ...messageBodyForm,
      {
        precondition: (values: any) => (!preview || reply) && !editing,
        name: '',
        itemType: 'button',
        jItemProps: { type: 'submit' },
        label: 'SEND',
        gridProps: { xs: 6, mx: 'auto' },
      },
    ];
  }
};
